// eslint-disable-next-line no-use-before-define
import React, { useEffect, useState, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import dayjs from 'op-utils/dayjs';
import { LoadingSpinner } from 'shared-components/components';
import PhotoWidget from 'shared-components/components/UIFormComponents/PhotoWidget';
import { PATIENT_SEARCH_RO } from './Queries';
import { COPY } from './constants';
import { Region } from 'shared-components/enums';
import { calculateMomentAge } from 'shared-components/utils';
import { RoleType } from 'op-enums';
import {
  Box,
  TextField,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  IconButton,
  styled,
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import { formatPatientName } from 'op-utils/PatientHelper';
import { CustomTheme } from '@mui/material/styles';
import { useActionPopoverContext } from 'op-contexts';

const { MO } = RoleType;

interface ROInlinePatientSearchProps {
  closeSearch: () => void;
  recentPatients?: any;
  primaryRole: string;
}

interface patientRecordType {
  id: string;
  fullName: string;
  ida: string;
  city: string;
  dob: string;
  line1: string;
  line2: string;
  postcode: string;
  state: string;
  emrInstance: string;
}

const EMPTY_SEARCH_RESULTS_HELPERS = [
  { text: COPY.CHECK_SPELLING },
  { text: COPY.CHECK_ID_TRY_AGAIN },
  { text: COPY.NEW_MOSAIQ_PATIENT_WAIT },
  { text: COPY.NAME_OR_ID },
];

const REACT_APP_REGION = import.meta.env.REACT_APP_REGION;

const formatAddress = (address: patientRecordType): string => {
  const mappedAddress = ('address' in address ? address['address'] : address) as patientRecordType;
  const addressFields = ['line1', 'line2', 'city', 'state', 'postcode'];
  if (!mappedAddress) return '';
  const addressValues = addressFields.map((key) => {
    // @ts-ignore
    if (key in mappedAddress) return mappedAddress[key];
    return '';
  });
  const addressFiltered = addressValues.filter((value) => value && value !== '');
  return addressFiltered.join(', ');
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '0.75rem',
  verticalAlign: 'middle',
  borderBottom: `1px solid ${theme.palette.divider}`,
  height: '64px !important',
  fontWeight: 'normal',
}));

const EmptySearchResults = ({ searchText }: { searchText: string }): JSX.Element => {
  const theme = useTheme<CustomTheme>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography
        variant="subtitle1"
        sx={{
          margin: '8px 0 0 0',
          paddingLeft: '16px',
        }}>
        {`${COPY.NO_RESULTS_MATCHING} "${searchText}"`}
      </Typography>
      <Typography
        variant="overline"
        component="span"
        color={theme.palette.text.secondary}
        sx={{
          padding: '8px 0 0 16px',
        }}>
        SEARCH TIPS
      </Typography>
      <Box
        component="ul"
        sx={{
          marginTop: '8px',
          paddingLeft: '16px',
          listStyle: 'none',
        }}>
        {EMPTY_SEARCH_RESULTS_HELPERS.map((data, index) => (
          <Box component="li" key={`search-tip-${index}`}>
            <Typography
              variant="body1"
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
              }}>
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '50%',
                  marginRight: '16px',
                }}
              />
              {data.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ROInlinePatientSearch = (props: ROInlinePatientSearchProps): JSX.Element => {
  const [focused, setFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [cursor, setCursor] = useState(-1);
  const history = useHistory();
  const theme = useTheme<CustomTheme>();
  const isUkRegion = REACT_APP_REGION === Region.UK;
  const { closeFloatingPanel } = useActionPopoverContext();

  const {
    loading,
    error,
    data: searchList,
  } = useQuery(PATIENT_SEARCH_RO, {
    variables: { search: searchText },
    fetchPolicy: 'network-only',
    skip: searchText === '',
  });

  const [patientsList, setPatientsList] = useState<patientRecordType[]>([]);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    closeFloatingPanel();
  }, [closeFloatingPanel]);

  useEffect(() => {
    if (cursor >= 0) {
      const element = document.getElementById('patRecord' + cursor);
      if (element) {
        element.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      }
      // Keep focus on input for keyboard navigation
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [cursor]);

  useEffect(() => {
    if (searchText === '') {
      setPatientsList(props.recentPatients || []);
      setCursor(-1);
    }
  }, [searchText, props.recentPatients]);

  // Show loading indicator only after a delay to avoid flickering
  useEffect(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (loading && searchText !== '') {
      loadingTimeoutRef.current = setTimeout(() => {
        setShowLoading(true);
      }, 500);
    } else {
      setShowLoading(false);
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [loading, searchText]);

  // Update patientsList when have new data, rather than when loading starts
  useEffect(() => {
    if (searchText !== '' && searchList) {
      const searchResults = searchList.searchPatientsRo || [];
      setPatientsList(searchResults);
      setCursor(-1);
    }
  }, [searchList, searchText]);

  const openPatientRecord = useCallback(
    (id: string | number): void => {
      props.closeSearch();
      if (props.primaryRole === MO) {
        history.push(`/medonc/patient/${id}/summary`);
      } else {
        history.push(`/radiation/patient/${id}/summary`);
      }
    },
    [props.primaryRole, props.closeSearch, history],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((prevCursor) => {
          const nextCursor = prevCursor < 0 ? 0 : prevCursor + 1;
          return nextCursor < patientsList.length ? nextCursor : prevCursor;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((prevCursor) => {
          if (prevCursor <= 0) {
            return -1;
          }
          return prevCursor - 1;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (cursor >= 0 && cursor < patientsList.length) {
          openPatientRecord(patientsList[cursor].id);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        if (patientsList.length > 0) {
          setCursor(0);
        }
      } else if (e.key === 'End') {
        e.preventDefault();
        if (patientsList.length > 0) {
          setCursor(patientsList.length - 1);
        }
      }
    },
    [cursor, patientsList, openPatientRecord, props.closeSearch],
  );

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        props.closeSearch();
      }
    };

    const handleSearchClick = (e: MouseEvent) => {
      const wrapper = document.getElementById('patientSearchWrapper');
      if (wrapper && !wrapper.contains(e.target as Node)) {
        props.closeSearch();
      }
    };

    document.addEventListener('click', handleSearchClick, false);
    document.addEventListener('keydown', handleEscape, false);
    return () => {
      document.removeEventListener('click', handleSearchClick, false);
      document.removeEventListener('keydown', handleEscape, false);
    };
  }, [props.closeSearch]);

  const debouncedSetSearchTextRef = useRef(
    debounce((value: string) => {
      setSearchText(value);
    }, 300),
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const trimmedValue = value.trim();
    setInputValue(value); // update immediately for responsive typing
    // Enforce a 2-character minimum before triggering any search queries.
    if (trimmedValue.length < 2) {
      debouncedSetSearchTextRef.current.cancel();
      setSearchText('');
      setCursor(-1);
      return;
    }

    debouncedSetSearchTextRef.current(trimmedValue); // but debounce the actual search
  }, []);

  const handleClearSearch = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    debouncedSetSearchTextRef.current.cancel();
    setInputValue('');
    setSearchText('');
    setCursor(-1);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  }, []);

  const title = searchText === '' ? 'Recently Viewed' : 'Patient Search';

  return (
    <Box
      id="patientSearchWrapper"
      sx={{
        position: 'absolute',
        zIndex: 50,
        right: '120px',
        top: '12px',
        width: '70vw',
        maxWidth: '768px',
      }}>
      {/* Search Input */}
      <Box
        role="combobox"
        aria-expanded={patientsList ? patientsList.length > 0 : false}
        aria-haspopup="listbox"
        sx={{
          height: '40px',
          width: '70vw',
          maxWidth: '768px',
          border: `1px solid ${theme.palette.secondary.main}`,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          borderColor: focused ? theme.palette.primary.main : theme.palette.secondary.main,
          backgroundColor: theme.palette.primary.contrastText,
          '& svg': {
            margin: '8px',
          },
        }}>
        <Search color="primary" aria-hidden="true" />
        <TextField
          inputRef={inputRef}
          fullWidth
          variant="standard"
          autoFocus
          value={inputValue}
          onBlur={() => setFocused(false)}
          onFocus={() => setFocused(true)}
          onKeyDown={handleKeyDown}
          onChange={handleInputChange}
          type="text"
          data-testid="patient-search-input"
          placeholder="Search patient name or patient ID…"
          aria-label="Search patient name or patient ID"
          aria-controls="inlinePatientSearchWrapper"
          aria-activedescendant={cursor >= 0 ? `patRecord${cursor}` : undefined}
          aria-autocomplete="list"
          InputProps={{ disableUnderline: true }}
          sx={{
            '.MuiInputBase-root:hover': {
              backgroundColor: 'white',
            },
            '& input': {
              border: 'none',
              width: '100%',
              fontSize: '16px',
              lineHeight: '24px',
            },
          }}
        />
        {inputValue && (
          <IconButton
            onClick={handleClearSearch}
            aria-label="Clear search"
            size="small"
            sx={{
              marginRight: '4px',
              padding: '0px',
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: 'transparent',
              },
            }}
            data-testid="clear-search-button">
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Search Results Container */}
      <Box
        id="inlinePatientSearchWrapper"
        role="listbox"
        aria-label="Patient search results"
        sx={{
          boxSizing: 'border-box',
          zIndex: 50,
          position: 'relative',
          top: '6px',
          width: '70vw',
          maxWidth: '768px',
          height: '70vh',
          backgroundColor: theme.palette.background.default,
          boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.2)',
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}>
        {showLoading ? (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingSpinner relativeSpinner={true} loadingText={'Searching for ' + searchText} />
          </Box>
        ) : (
          <>
            <Typography
              variant="overline"
              color={theme.palette.text.secondary}
              data-test-id="search-title"
              sx={{ padding: '8px 0 0 16px' }}>
              {title}
            </Typography>

            {searchText !== '' && patientsList && patientsList?.length === 0 && !loading && (
              <EmptySearchResults searchText={searchText} />
            )}

            {searchText === '' && patientsList && patientsList?.length === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body1" sx={{ marginTop: '0px', padding: '10px 0 0 16px' }}>
                  {COPY.EMPTY_RECENT_PAT_LIST}
                </Typography>
              </Box>
            )}

            {!error && patientsList && patientsList?.length > 0 && (
              <Box
                sx={{
                  display: 'table',
                  height: '100%',
                  width: '100%',
                  fontSize: '16px',
                  lineHeight: '24px',
                }}>
                <Table data-test-id="pat-search-table">
                  <TableBody>
                    {patientsList?.map((patient: any, index: number) => {
                      const formattedDateOfBirth = patient.dob ? dayjs(patient.dob).format('DD-MMM-YYYY') : '-';

                      const displayName = formatPatientName(
                        patient.firstName,
                        patient.middleName,
                        patient.preferredName,
                        patient.lastName,
                        patient.fullName,
                      );

                      const isSelected = cursor === index;
                      return (
                        <TableRow
                          key={patient.id}
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => openPatientRecord(patient.id)}
                          onMouseOver={() => setCursor(index)}
                          onMouseOut={() => setCursor(-1)}
                          id={'patRecord' + index}
                          sx={{
                            maxHeight: '64px',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: theme.palette.grey[100],
                            },
                            ...(cursor === index && {
                              backgroundColor: theme.palette.grey[100],
                            }),
                          }}>
                          <StyledTableCell sx={{ paddingLeft: '28px' }}>
                            <PhotoWidget patientId={isUkRegion ? null : patient.id} enableOverlay={true} size={'sm'} />
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: '30%', maxWidth: '108px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body1" data-test-id="patient-name">
                                {displayName}
                              </Typography>
                              <Typography
                                variant="body2"
                                data-test-id="patient-ida-emr"
                                sx={{
                                  lineHeight: '15px',
                                  overflowWrap: 'break-word',
                                  color: theme.palette.text.secondary,
                                }}>
                                EMR: {patient.emrInstance || '-'} | ID: {patient.ida}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: '20%' }}>
                            <Typography variant="body1">{formattedDateOfBirth}</Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                              Age: {patient.dob ? calculateMomentAge(dayjs(patient.dob)) : '-'}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: '50%' }}>
                            <Typography variant="body1" data-test-id="patient-address">
                              {formatAddress(patient)}
                            </Typography>
                          </StyledTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ROInlinePatientSearch;
